import type { ApiErrorCode } from '@/types/api';

const BASE_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000') + '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: ApiErrorCode | string,
    /** Detalle por campo, p. ej. los incumplimientos de la politica de claves. */
    readonly errors?: string[],
    readonly retryAfterSeconds?: number,
    readonly remainingAttempts?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * El access token vive solo en memoria. No se guarda en localStorage a
 * proposito: si un script malicioso se cuela en la pagina, no encuentra un
 * token persistido que robar. La sesion sobrevive a un refresco de pagina
 * gracias a la cookie httpOnly del refresh token, que JavaScript no puede leer.
 */
let accessToken: string | null = null;

/** Se avisa cuando la sesion muere, para que el store limpie y redirija. */
type SessionEndedListener = (reason: string) => void;
const sessionEndedListeners = new Set<SessionEndedListener>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onSessionEnded(listener: SessionEndedListener): () => void {
  sessionEndedListeners.add(listener);
  return () => sessionEndedListeners.delete(listener);
}

function notifySessionEnded(reason: string): void {
  for (const listener of sessionEndedListeners) {
    listener(reason);
  }
}

/**
 * Refresco compartido: si varias peticiones reciben 401 a la vez, todas esperan
 * al mismo intento en lugar de disparar un refresh cada una (lo que rotaria el
 * token varias veces y acabaria invalidando la sesion).
 */
let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        return false;
      }
      const data = (await response.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Se libera en el siguiente tick para que las peticiones que entraron
      // durante el refresco compartan este mismo resultado.
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Uso interno: evita entrar en bucle si el reintento vuelve a dar 401. */
  skipRetry?: boolean;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipRetry, headers, ...rest } = options;

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && !skipRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return apiFetch<T>(path, { ...options, skipRetry: true });
    }

    accessToken = null;
    const payload = await safeJson(response);
    notifySessionEnded(normalizeMessage(payload));
    throw toApiError(response.status, payload);
  }

  if (!response.ok) {
    throw toApiError(response.status, await safeJson(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

interface ErrorPayload {
  message?: string | string[];
  code?: string;
  errors?: string[];
  retryAfterSeconds?: number;
  remainingAttempts?: number;
}

async function safeJson(response: Response): Promise<ErrorPayload | null> {
  try {
    return (await response.json()) as ErrorPayload;
  } catch {
    return null;
  }
}

/** NestJS devuelve un array de mensajes cuando falla la validacion del DTO. */
function normalizeMessage(payload: ErrorPayload | null): string {
  if (Array.isArray(payload?.message)) {
    return payload.message.join('. ');
  }
  return payload?.message ?? 'No se pudo completar la operacion';
}

function toApiError(status: number, payload: ErrorPayload | null): ApiError {
  return new ApiError(
    status,
    normalizeMessage(payload),
    payload?.code,
    payload?.errors,
    payload?.retryAfterSeconds,
    payload?.remainingAttempts,
  );
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  /** Intenta recuperar la sesion al cargar la app usando la cookie httpOnly. */
  restoreSession: refreshAccessToken,
};

export { BASE_URL };
